import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  IoAlertCircleOutline,
  IoBagHandleOutline,
  IoBarChartOutline,
  IoCheckmarkCircle,
  IoChevronForward,
  IoFootstepsOutline,
  IoHelpBuoyOutline,
  IoMailOutline,
  IoTimeOutline,
} from "react-icons/io5";
import styles from "./Contact.module.css";

const baseURL = process.env.REACT_APP_BACKEND_API_URL;
const contactSubmitAPI = "api/contact/submit";

const buildContactUrl = () => {
  if (!baseURL) {
    return "";
  }
  return `${baseURL.replace(/\/+$/, "")}/${contactSubmitAPI}`;
};

type InquiryType = "contact" | "support";

type FormState = {
  inquiry_type: InquiryType;
  name: string;
  email: string;
  subject: string;
  message: string;
  company: string;
  phone: string;
  category: string;
  order_id: string;
  customer_id: string;
  priority: string;
};

const initialContactState: FormState = {
  inquiry_type: "contact",
  name: "",
  email: "",
  subject: "",
  message: "",
  company: "",
  phone: "",
  category: "general",
  order_id: "",
  customer_id: "",
  priority: "normal",
};

const initialSupportState: FormState = {
  inquiry_type: "support",
  name: "",
  email: "",
  subject: "",
  message: "",
  company: "",
  phone: "",
  category: "technical",
  order_id: "",
  customer_id: "",
  priority: "normal",
};

const EXPECTATIONS = [
  "Most requests get a first response within one business day.",
  "Use Contact us for new sales and partnership requests.",
  "Use Customer support if you already use mRehab. Include your order ID.",
];

function charCountClass(charsLeft: number) {
  return `ui-help ${styles.charCount} ${charsLeft < 100 ? styles.charCountLow : ""}`;
}

export default function Contact() {
  const [activePanel, setActivePanel] = useState<InquiryType>("contact");
  const [contactData, setContactData] = useState<FormState>(initialContactState);
  const [supportData, setSupportData] = useState<FormState>(initialSupportState);
  const [submitting, setSubmitting] = useState<InquiryType | null>(null);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  const contactCharsLeft = useMemo(
    () => 2000 - contactData.message.length,
    [contactData.message.length]
  );
  const supportCharsLeft = useMemo(
    () => 2000 - supportData.message.length,
    [supportData.message.length]
  );

  const handleContactChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setContactData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSupportChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setSupportData((prev) => ({ ...prev, [name]: value }));
  };

  const submitInquiry = async (payload: FormState) => {
    const contactUrl = buildContactUrl();
    if (!contactUrl) {
      throw new Error("Missing REACT_APP_BACKEND_API_URL");
    }

    await axios.post(contactUrl, payload);
  };

  const handleContactSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");
    setSubmitting("contact");

    try {
      await submitInquiry(contactData);
      setSuccessMessage(
        "Thanks for reaching out. The contact-us team received your request and will reply shortly."
      );
      setContactData(initialContactState);
    } catch (submitError: any) {
      setError(
        submitError?.response?.data?.detail ||
          submitError?.message ||
          "Could not send your request right now."
      );
    } finally {
      setSubmitting(null);
    }
  };

  const handleSupportSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");
    setSubmitting("support");

    try {
      await submitInquiry(supportData);
      setSuccessMessage(
        "Support request submitted. The support team received your ticket and will follow up soon."
      );
      setSupportData(initialSupportState);
    } catch (submitError: any) {
      setError(
        submitError?.response?.data?.detail ||
          submitError?.message ||
          "Could not send your support request right now."
      );
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <main id="main" className="ui-page">
      <div className="ui-container">
        <header className="ui-page-header">
          <h1>Talk to the right team</h1>
          <p>
            New inquiries go to <strong>contact-us@mrehab.agency</strong>. Existing
            customer help goes to <strong>support@mrehab.agency</strong>.
          </p>
        </header>

        <div className={styles.layout}>
          <div className={styles.channels}>
            <div
              className="ui-segmented ui-segmented--full"
              role="tablist"
              aria-label="Contact route"
            >
              <button
                type="button"
                role="tab"
                id="contact-tab"
                aria-selected={activePanel === "contact"}
                aria-controls="contact-panel"
                onClick={() => setActivePanel("contact")}
              >
                New customer contact
              </button>
              <button
                type="button"
                role="tab"
                id="support-tab"
                aria-selected={activePanel === "support"}
                aria-controls="support-panel"
                onClick={() => setActivePanel("support")}
              >
                Existing customer support
              </button>
            </div>

            {error && (
              <div className="ui-banner ui-banner--error" role="alert">
                <IoAlertCircleOutline aria-hidden />
                <p>{error}</p>
              </div>
            )}
            {successMessage && (
              <div className="ui-banner ui-banner--success" role="status">
                <IoCheckmarkCircle aria-hidden />
                <p>{successMessage}</p>
              </div>
            )}

            <article
              id="contact-panel"
              role="tabpanel"
              aria-labelledby="contact-tab"
              className={`ui-card ui-card--lg ${
                activePanel === "contact" ? styles.cardVisible : styles.cardHidden
              }`}
            >
              <div className="ui-card-header">
                <IoMailOutline aria-hidden />
                <div className="ui-card-header-text">
                  <h2>Contact us</h2>
                  <p>
                    Sales, partnerships, media, and onboarding questions for new
                    customers.
                  </p>
                </div>
              </div>

              <form className={styles.form} onSubmit={handleContactSubmit}>
                <div className="ui-field-grid">
                  <label className="ui-field">
                    <span className="ui-label">Full name</span>
                    <input
                      className="ui-input"
                      name="name"
                      value={contactData.name}
                      onChange={handleContactChange}
                      required
                      maxLength={120}
                    />
                  </label>
                  <label className="ui-field">
                    <span className="ui-label">Work email</span>
                    <input
                      className="ui-input"
                      type="email"
                      name="email"
                      value={contactData.email}
                      onChange={handleContactChange}
                      required
                      maxLength={200}
                    />
                  </label>
                </div>

                <div className="ui-field-grid">
                  <label className="ui-field">
                    <span className="ui-label">
                      Company <span className="ui-label-optional">(optional)</span>
                    </span>
                    <input
                      className="ui-input"
                      name="company"
                      value={contactData.company}
                      onChange={handleContactChange}
                      maxLength={160}
                    />
                  </label>
                  <label className="ui-field">
                    <span className="ui-label">
                      Phone <span className="ui-label-optional">(optional)</span>
                    </span>
                    <input
                      className="ui-input"
                      name="phone"
                      value={contactData.phone}
                      onChange={handleContactChange}
                      maxLength={40}
                    />
                  </label>
                </div>

                <div className="ui-field-grid">
                  <label className="ui-field">
                    <span className="ui-label">Inquiry type</span>
                    <select
                      className="ui-input"
                      name="category"
                      value={contactData.category}
                      onChange={handleContactChange}
                      required
                    >
                      <option value="general">General question</option>
                      <option value="sales">Sales</option>
                      <option value="partnership">Partnership</option>
                      <option value="provider">Provider onboarding</option>
                      <option value="media">Media</option>
                    </select>
                  </label>
                  <label className="ui-field">
                    <span className="ui-label">Subject</span>
                    <input
                      className="ui-input"
                      name="subject"
                      value={contactData.subject}
                      onChange={handleContactChange}
                      required
                      maxLength={160}
                    />
                  </label>
                </div>

                <label className="ui-field">
                  <span className="ui-label">Message</span>
                  <textarea
                    className="ui-input"
                    name="message"
                    value={contactData.message}
                    onChange={handleContactChange}
                    required
                    minLength={20}
                    maxLength={2000}
                    rows={6}
                  />
                  <span className={charCountClass(contactCharsLeft)}>
                    {contactCharsLeft} characters left
                  </span>
                </label>

                <div className={styles.formFooter}>
                  <button
                    type="submit"
                    className={`ui-btn ${styles.submit}`}
                    disabled={submitting === "contact"}
                    aria-busy={submitting === "contact"}
                  >
                    {submitting === "contact" ? (
                      <>
                        <span className="ui-spinner" aria-hidden />
                        Sending...
                      </>
                    ) : (
                      "Send message"
                    )}
                  </button>
                </div>
              </form>
            </article>

            <article
              id="support-panel"
              role="tabpanel"
              aria-labelledby="support-tab"
              className={`ui-card ui-card--lg ${
                activePanel === "support" ? styles.cardVisible : styles.cardHidden
              }`}
            >
              <div className="ui-card-header">
                <IoHelpBuoyOutline aria-hidden />
                <div className="ui-card-header-text">
                  <h2>Customer support</h2>
                  <p>Product, account, or order help for current mRehab customers.</p>
                </div>
              </div>

              <form className={styles.form} onSubmit={handleSupportSubmit}>
                <div className="ui-field-grid">
                  <label className="ui-field">
                    <span className="ui-label">Full name</span>
                    <input
                      className="ui-input"
                      name="name"
                      value={supportData.name}
                      onChange={handleSupportChange}
                      required
                      maxLength={120}
                    />
                  </label>
                  <label className="ui-field">
                    <span className="ui-label">Account email</span>
                    <input
                      className="ui-input"
                      type="email"
                      name="email"
                      value={supportData.email}
                      onChange={handleSupportChange}
                      required
                      maxLength={200}
                    />
                  </label>
                </div>

                <div className="ui-field-grid">
                  <label className="ui-field">
                    <span className="ui-label">
                      Customer ID <span className="ui-label-optional">(optional)</span>
                    </span>
                    <input
                      className="ui-input"
                      name="customer_id"
                      value={supportData.customer_id}
                      onChange={handleSupportChange}
                      maxLength={80}
                    />
                  </label>
                  <label className="ui-field">
                    <span className="ui-label">
                      Order ID <span className="ui-label-optional">(optional)</span>
                    </span>
                    <input
                      className="ui-input"
                      name="order_id"
                      value={supportData.order_id}
                      onChange={handleSupportChange}
                      maxLength={80}
                    />
                  </label>
                </div>

                <div className="ui-field-grid">
                  <label className="ui-field">
                    <span className="ui-label">Issue type</span>
                    <select
                      className="ui-input"
                      name="category"
                      value={supportData.category}
                      onChange={handleSupportChange}
                      required
                    >
                      <option value="technical">Technical issue</option>
                      <option value="billing">Billing</option>
                      <option value="device">Device setup</option>
                      <option value="account">Account access</option>
                      <option value="clinical">Clinical workflow</option>
                    </select>
                  </label>
                  <label className="ui-field">
                    <span className="ui-label">Priority</span>
                    <select
                      className="ui-input"
                      name="priority"
                      value={supportData.priority}
                      onChange={handleSupportChange}
                      required
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                    </select>
                  </label>
                </div>

                <label className="ui-field">
                  <span className="ui-label">Subject</span>
                  <input
                    className="ui-input"
                    name="subject"
                    value={supportData.subject}
                    onChange={handleSupportChange}
                    required
                    maxLength={160}
                  />
                </label>

                <label className="ui-field">
                  <span className="ui-label">Describe your issue</span>
                  <textarea
                    className="ui-input"
                    name="message"
                    value={supportData.message}
                    onChange={handleSupportChange}
                    required
                    minLength={20}
                    maxLength={2000}
                    rows={6}
                  />
                  <span className={charCountClass(supportCharsLeft)}>
                    {supportCharsLeft} characters left
                  </span>
                </label>

                <div className={styles.formFooter}>
                  <button
                    type="submit"
                    className={`ui-btn ${styles.submit}`}
                    disabled={submitting === "support"}
                    aria-busy={submitting === "support"}
                  >
                    {submitting === "support" ? (
                      <>
                        <span className="ui-spinner" aria-hidden />
                        Sending...
                      </>
                    ) : (
                      "Send support request"
                    )}
                  </button>
                </div>
              </form>
            </article>
          </div>

          <aside className={styles.sidebar} aria-label="What to expect">
            <section className="ui-card" aria-labelledby="expect-title">
              <div className="ui-card-header">
                <IoTimeOutline aria-hidden />
                <div className="ui-card-header-text">
                  <h2 id="expect-title">What to expect</h2>
                </div>
              </div>
              <ul className="ui-bullets">
                {EXPECTATIONS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="ui-card" aria-labelledby="quick-links-title">
              <h2 id="quick-links-title">Quick links</h2>
              <div className="ui-list">
                  <Link to="/how-it-works" className="ui-list-row">
                    <span className="ui-icon-chip" aria-hidden>
                      <IoFootstepsOutline />
                    </span>
                    <span className="ui-list-text">
                      <span className="ui-list-label">How mRehab works</span>
                    </span>
                    <span className="ui-list-trailing">
                      <IoChevronForward aria-hidden />
                    </span>
                  </Link>
                  <Link to="/research" className="ui-list-row">
                    <span className="ui-icon-chip" aria-hidden>
                      <IoBarChartOutline />
                    </span>
                    <span className="ui-list-text">
                      <span className="ui-list-label">Research and results</span>
                    </span>
                    <span className="ui-list-trailing">
                      <IoChevronForward aria-hidden />
                    </span>
                  </Link>
                  <Link to="/shopping" className="ui-list-row">
                    <span className="ui-icon-chip" aria-hidden>
                      <IoBagHandleOutline />
                    </span>
                    <span className="ui-list-text">
                      <span className="ui-list-label">Starter kit</span>
                    </span>
                    <span className="ui-list-trailing">
                      <IoChevronForward aria-hidden />
                    </span>
                  </Link>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </main>
  );
}
